import React, { useState, useMemo } from "react";
import {
  BookOpen, Search, ChevronRight, X, Clock, Tag, Lightbulb, Mic2, Sparkles,
  Users, ShieldCheck, BarChart3, Zap, Video, FileText, ChevronDown, ChevronUp,
  PlayCircle, Settings, HelpCircle, ArrowRight,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

// ─────────────────────────────────────────────────────────────────────────────
// Article data — purely static, no backend needed
// ─────────────────────────────────────────────────────────────────────────────
const CATEGORIES = [
  { id: "all",          label: "Semua Artikel",        icon: BookOpen,   color: "text-slate-600" },
  { id: "memulai",      label: "Memulai",              icon: PlayCircle, color: "text-blue-600"  },
  { id: "rapat",        label: "Rapat & Transkrip",    icon: Video,      color: "text-violet-600" },
  { id: "ai",           label: "AI & Summary",         icon: Sparkles,   color: "text-amber-600" },
  { id: "voice",        label: "Voice Biometrics",     icon: Mic2,       color: "text-emerald-600"},
  { id: "admin",        label: "Administrasi",         icon: ShieldCheck,color: "text-red-600"   },
  { id: "tips",         label: "Tips & Trik",          icon: Lightbulb,  color: "text-orange-500"},
];

const ARTICLES = [
  // ── MEMULAI ───────────────────────────────────────────────────────────────
  {
    id: "gs-1", category: "memulai", readTime: 3,
    title: "Cara Memulai Meeting Pertama Anda",
    summary: "Panduan langkah demi langkah untuk membuat dan memulai rapat pertama di VECTRA.",
    content: [
      { type: "paragraph", text: "VECTRA memudahkan Anda membuat dan mendokumentasikan rapat secara otomatis menggunakan AI. Ikuti langkah berikut untuk memulai rapat pertama Anda." },
      { type: "heading", text: "Langkah 1 — Buat Rapat Baru" },
      { type: "steps", items: [
        "Klik menu Meeting di sidebar kiri.",
        "Tekan tombol + Buat Meeting di pojok kanan atas.",
        "Isi judul, tanggal, lokasi, dan agenda rapat.",
        "Tambahkan peserta dari daftar pengguna terdaftar.",
        "Klik Simpan untuk menyimpan rapat.",
      ]},
      { type: "heading", text: "Langkah 2 — Mulai Transkripsi" },
      { type: "paragraph", text: "Setelah rapat dibuat, Anda dapat memilih dua metode transkripsi:" },
      { type: "steps", items: [
        "Upload Audio: Upload file rekaman rapat (MP3/MP4/WAV) untuk ditranskripsi otomatis oleh Whisper AI.",
        "Live Meeting: Aktifkan mikrofon dan transkripsi real-time langsung saat rapat berlangsung.",
      ]},
      { type: "tip", text: "Pastikan kualitas audio baik — hindari kebisingan latar belakang untuk hasil transkripsi yang lebih akurat." },
    ],
  },
  {
    id: "gs-2", category: "memulai", readTime: 4,
    title: "Mengenal Antarmuka VECTRA",
    summary: "Panduan lengkap tentang bagian-bagian utama antarmuka VECTRA dan fungsinya.",
    content: [
      { type: "paragraph", text: "VECTRA dirancang dengan antarmuka yang bersih dan intuitif. Berikut panduan mengenal elemen-elemen utamanya." },
      { type: "heading", text: "Sidebar Navigasi" },
      { type: "steps", items: [
        "Ringkasan: Dashboard untuk statistik dan Calendar untuk jadwal rapat.",
        "Rapat: Kelola meeting, lihat transkrip live, AI Summary, dan daftar peserta.",
        "Intelijen: Pencarian global, laporan, dan manajemen Voice Biometrics.",
        "Administrasi: Kelola pengguna, role, dan pengaturan sistem (khusus admin).",
      ]},
      { type: "heading", text: "Dashboard" },
      { type: "paragraph", text: "Dashboard menampilkan statistik rapat terkini: total rapat, rapat hari ini, action items tertunda, akurasi transkripsi rata-rata, dan top pembicara." },
      { type: "heading", text: "Profil & Pengaturan" },
      { type: "paragraph", text: "Klik avatar di pojok kanan atas untuk mengakses profil, ganti password, dan logout." },
      { type: "tip", text: "Gunakan fitur Search untuk mencari transkrip, rapat, atau pembicara secara cepat di seluruh database." },
    ],
  },

  // ── RAPAT & TRANSKRIP ─────────────────────────────────────────────────────
  {
    id: "rt-1", category: "rapat", readTime: 5,
    title: "Cara Membuat dan Menjadwalkan Rapat",
    summary: "Pelajari cara membuat, menjadwalkan, dan mengatur detail rapat di VECTRA.",
    content: [
      { type: "heading", text: "Membuat Rapat Baru" },
      { type: "steps", items: [
        "Buka halaman Meeting dari sidebar.",
        "Klik tombol + Buat Meeting.",
        "Isi form: Judul Rapat, Tanggal & Waktu, Lokasi (opsional), Divisi, Bahasa, Agenda.",
        "Tambahkan peserta dengan memilih dari dropdown daftar pengguna.",
        "Klik Simpan — rapat siap digunakan.",
      ]},
      { type: "heading", text: "Mengelola Rapat yang Ada" },
      { type: "paragraph", text: "Dari halaman daftar Meeting, Anda bisa melihat semua rapat dengan status: Draft, Live, Completed, atau Archived. Klik rapat untuk membuka detail, edit, atau hapus." },
      { type: "heading", text: "Status Rapat" },
      { type: "steps", items: [
        "Draft: Rapat baru dibuat, belum ada rekaman atau transkripsi.",
        "Live: Rapat sedang berlangsung dengan transkripsi real-time aktif.",
        "Completed: Rapat selesai, transkrip dan summary tersedia.",
        "Archived: Rapat diarsipkan dari tampilan utama.",
      ]},
      { type: "tip", text: "Gunakan Calendar untuk melihat jadwal rapat dalam tampilan bulanan dan merencanakan agenda ke depan." },
    ],
  },
  {
    id: "rt-2", category: "rapat", readTime: 4,
    title: "Upload Audio untuk Transkripsi Otomatis",
    summary: "Cara mengupload file rekaman rapat untuk ditranskripsi secara otomatis menggunakan Whisper AI.",
    content: [
      { type: "heading", text: "Format Audio yang Didukung" },
      { type: "paragraph", text: "VECTRA mendukung format: MP3, MP4, M4A, WAV, WEBM, OGG. Ukuran file maksimal 200MB." },
      { type: "heading", text: "Cara Upload" },
      { type: "steps", items: [
        "Buka detail rapat yang ingin ditranskripsi.",
        "Klik area upload atau tombol Upload Rekaman.",
        "Pilih file audio dari komputer Anda.",
        "Tunggu proses transkripsi selesai (biasanya 1-3 menit per jam audio).",
        "Hasil transkrip langsung muncul di tab Transcript.",
      ]},
      { type: "heading", text: "Setelah Transkripsi" },
      { type: "paragraph", text: "Setelah transkripsi selesai, Anda dapat: membuat AI Summary, melihat action items, menggunakan AI Chat untuk bertanya tentang isi rapat, dan mengekspor hasil ke PDF/DOCX." },
      { type: "tip", text: "Untuk akurasi terbaik, gunakan rekaman dengan bit rate minimal 128kbps dan hindari audio yang terlalu terkompresi." },
    ],
  },
  {
    id: "rt-3", category: "rapat", readTime: 6,
    title: "Menggunakan Fitur Live Meeting (Realtime Transcript)",
    summary: "Panduan lengkap mengaktifkan transkripsi real-time selama rapat berlangsung.",
    content: [
      { type: "heading", text: "Persiapan Sebelum Live Meeting" },
      { type: "steps", items: [
        "Pastikan voiceprint peserta sudah di-enroll di menu Voice Biometrics.",
        "Pastikan koneksi internet stabil (min 5 Mbps upload).",
        "Gunakan mikrofon berkualitas baik (hindari built-in laptop di ruang berisik).",
        "Buat rapat terlebih dahulu dan tambahkan semua peserta.",
      ]},
      { type: "heading", text: "Memulai Live Transcript" },
      { type: "steps", items: [
        "Buka halaman Live Meeting dari sidebar.",
        "Pilih rapat yang sedang berlangsung dari dropdown.",
        "Klik Mulai Rekam dan izinkan akses mikrofon.",
        "Transkripsi akan muncul secara real-time di layar.",
        "Klik Hentikan Rekam saat rapat selesai.",
      ]},
      { type: "heading", text: "Identifikasi Pembicara Otomatis" },
      { type: "paragraph", text: "Jika semua peserta sudah enroll voiceprint, VECTRA akan secara otomatis mengidentifikasi siapa yang berbicara di setiap segmen transkrip." },
      { type: "tip", text: "Jika speaker tertukar, pastikan semua peserta memiliki voiceprint berkualitas baik (>80%). Rekam ulang voiceprint di ruangan yang tenang." },
    ],
  },
  {
    id: "rt-4", category: "rapat", readTime: 3,
    title: "Cara Export Hasil Rapat",
    summary: "Export transkrip, ringkasan, dan action items ke berbagai format file.",
    content: [
      { type: "heading", text: "Format Export yang Tersedia" },
      { type: "steps", items: [
        "PDF: Laporan lengkap dengan header perusahaan, cocok untuk arsip formal.",
        "DOCX: Dokumen Word yang bisa diedit untuk keperluan lebih lanjut.",
        "TXT: Teks plain transkrip lengkap.",
        "CSV: Data terstruktur, cocok untuk analisis di Excel.",
        "JSON: Data lengkap meeting dalam format mesin-readable.",
      ]},
      { type: "heading", text: "Cara Export" },
      { type: "steps", items: [
        "Buka detail rapat yang ingin di-export.",
        "Klik tab Export di bagian tab.",
        "Pilih format yang diinginkan.",
        "Klik tombol Download — file akan langsung terunduh.",
      ]},
      { type: "tip", text: "Export PDF menghasilkan laporan paling profesional dengan layout yang rapi, ideal untuk distribusi ke manajemen." },
    ],
  },

  // ── AI & SUMMARY ──────────────────────────────────────────────────────────
  {
    id: "ai-1", category: "ai", readTime: 4,
    title: "Menggunakan AI Summary untuk Meringkas Rapat",
    summary: "Cara membuat ringkasan otomatis rapat menggunakan Gemini AI.",
    content: [
      { type: "heading", text: "Apa itu AI Summary?" },
      { type: "paragraph", text: "AI Summary menggunakan Gemini 3 Flash untuk menganalisis transkrip rapat dan menghasilkan: ringkasan eksekutif, agenda, diskusi kunci, keputusan, risiko, dan action items secara otomatis." },
      { type: "heading", text: "Cara Membuat Summary" },
      { type: "steps", items: [
        "Pastikan transkrip rapat sudah tersedia.",
        "Buka detail rapat dan klik tab AI Summary.",
        "Klik tombol Generate Summary.",
        "Tunggu 10-30 detik untuk proses AI.",
        "Summary lengkap akan muncul dengan semua bagian terisi.",
      ]},
      { type: "heading", text: "Konten Summary" },
      { type: "steps", items: [
        "Ringkasan Eksekutif: Gambaran singkat seluruh rapat.",
        "Agenda & Diskusi: Topik yang dibahas secara berurutan.",
        "Keputusan: Semua keputusan yang diambil dalam rapat.",
        "Risiko: Potensi masalah yang perlu diantisipasi.",
        "Action Items: Daftar tugas dengan penanggung jawab dan deadline.",
      ]},
      { type: "tip", text: "Summary lebih akurat jika transkrip bersih dan speaker sudah teridentifikasi dengan benar. Re-generate jika hasilnya kurang memuaskan." },
    ],
  },
  {
    id: "ai-2", category: "ai", readTime: 3,
    title: "AI Chat — Tanya Jawab Berdasarkan Transkrip",
    summary: "Cara menggunakan fitur AI Chat untuk bertanya tentang isi rapat secara interaktif.",
    content: [
      { type: "heading", text: "Cara Menggunakan AI Chat" },
      { type: "steps", items: [
        "Buka detail rapat yang memiliki transkrip.",
        "Klik tab AI Chat.",
        "Ketik pertanyaan Anda di kolom chat.",
        "AI akan menjawab berdasarkan isi transkrip dengan timestamp referensi.",
      ]},
      { type: "heading", text: "Contoh Pertanyaan yang Bisa Diajukan" },
      { type: "steps", items: [
        "'Apa saja keputusan yang diambil dalam rapat ini?'",
        "'Siapa yang bertanggung jawab untuk proyek X?'",
        "'Kapan deadline untuk tugas Y?'",
        "'Apa risiko yang disebutkan dalam rapat?'",
        "'Rangkum diskusi tentang topik Z.'",
      ]},
      { type: "tip", text: "AI Chat HANYA menjawab berdasarkan isi transkrip. Jika informasi tidak ada dalam rapat, AI akan memberitahu Anda dengan jujur." },
    ],
  },
  {
    id: "ai-3", category: "ai", readTime: 3,
    title: "Sentiment Analysis — Analisis Suasana Rapat",
    summary: "Memahami laporan analisis sentimen untuk mengetahui suasana dan dinamika rapat.",
    content: [
      { type: "heading", text: "Apa itu Sentiment Analysis?" },
      { type: "paragraph", text: "Fitur Sentiment Analysis menganalisis nada bicara, pilihan kata, dan pola interaksi dalam transkrip untuk memberikan laporan suasana rapat: apakah positif, netral, tegang, atau negatif." },
      { type: "heading", text: "Cara Menghasilkan Laporan Sentimen" },
      { type: "steps", items: [
        "Buka detail rapat yang memiliki transkrip.",
        "Klik tab Sentimen.",
        "Klik tombol Analisis Sentimen.",
        "Tunggu AI menganalisis seluruh transkrip.",
        "Laporan lengkap akan muncul dengan skor, aspek, dan rekomendasi.",
      ]},
      { type: "heading", text: "Membaca Laporan Sentimen" },
      { type: "steps", items: [
        "Skor Keseluruhan: -1.0 (sangat negatif) hingga +1.0 (sangat positif).",
        "Aspek: Breakdown sentimen per topik (kolaborasi, kepemimpinan, dll).",
        "Per Pembicara: Nada bicara masing-masing peserta.",
        "Momen Kunci: Titik tegang dan titik positif dalam diskusi.",
        "Rekomendasi: Saran AI untuk meeting berikutnya.",
      ]},
      { type: "tip", text: "Gunakan laporan sentimen untuk evaluasi dinamika tim dan identifikasi potensi konflik atau peluang peningkatan kerja sama." },
    ],
  },

  // ── VOICE BIOMETRICS ──────────────────────────────────────────────────────
  {
    id: "vb-1", category: "voice", readTime: 5,
    title: "Cara Enrollment Voiceprint untuk Pengenalan Suara",
    summary: "Panduan mendaftarkan sampel suara (voiceprint) agar sistem dapat mengenali pembicara secara otomatis.",
    content: [
      { type: "heading", text: "Apa itu Voiceprint?" },
      { type: "paragraph", text: "Voiceprint adalah sidik suara unik setiap orang yang direkam dan disimpan sebagai data numerik. Sistem VECTRA menggunakan voiceprint untuk mengidentifikasi siapa yang berbicara di setiap segmen transkripsi." },
      { type: "heading", text: "Cara Mendaftarkan Voiceprint" },
      { type: "steps", items: [
        "Buka menu Voice Biometrics dari sidebar.",
        "Pilih pengguna yang akan di-enroll dari dropdown.",
        "Klik Mulai Rekam 20 Detik.",
        "Izinkan akses mikrofon saat diminta browser.",
        "Bicara normal selama 20 detik — bacakan teks, perkenalkan diri, atau ceritakan apa saja.",
        "Klik Selesai atau tunggu timer habis secara otomatis.",
        "Sistem akan memproses dan menyimpan voiceprint.",
      ]},
      { type: "heading", text: "Tips Rekam Voiceprint Berkualitas" },
      { type: "steps", items: [
        "Gunakan ruangan yang tenang dengan sedikit gema.",
        "Bicara dalam jarak 15-30 cm dari mikrofon.",
        "Gunakan nada bicara normal seperti saat rapat.",
        "Hindari berbisik atau berteriak.",
        "Pastikan indikator audio aktif (bar bergerak saat bicara).",
      ]},
      { type: "tip", text: "Kualitas voiceprint >80% sudah cukup untuk identifikasi yang andal. Re-enroll jika kualitas di bawah 70%." },
    ],
  },
  {
    id: "vb-2", category: "voice", readTime: 3,
    title: "Menguji Akurasi Pengenalan Suara",
    summary: "Cara menggunakan fitur Uji Akurasi Suara untuk memverifikasi voiceprint Anda.",
    content: [
      { type: "heading", text: "Fitur Uji Akurasi Suara" },
      { type: "paragraph", text: "Sebelum menggunakan Live Meeting, disarankan untuk menguji apakah sistem dapat mengenali suara Anda dengan benar menggunakan fitur Uji Akurasi Suara." },
      { type: "heading", text: "Cara Uji Suara" },
      { type: "steps", items: [
        "Buka halaman Voice Biometrics.",
        "Scroll ke bagian Uji Akurasi Suara di bawah.",
        "Klik Mulai Uji Suara.",
        "Bicara normal selama 10 detik.",
        "Klik Selesai atau tunggu timer habis.",
        "Sistem akan menampilkan hasil: siapa yang teridentifikasi dan persentase akurasi.",
      ]},
      { type: "heading", text: "Membaca Hasil Uji" },
      { type: "steps", items: [
        "Teridentifikasi: menunjukkan nama yang paling cocok dengan suara Anda.",
        "Kepercayaan: persentase keyakinan sistem (80%+ sangat baik, 60-80% cukup, <60% perlu re-enroll).",
        "Bar per peserta: skor kemiripan dengan setiap voiceprint yang terdaftar.",
      ]},
      { type: "tip", text: "Jika hasil uji menunjukkan identitas yang salah, lakukan enrollment ulang dengan suara yang lebih jelas dan di lingkungan yang lebih tenang." },
    ],
  },

  // ── ADMINISTRASI ──────────────────────────────────────────────────────────
  {
    id: "adm-1", category: "admin", readTime: 4,
    title: "Mengelola Pengguna dan Role",
    summary: "Panduan untuk admin dalam menambah, mengedit, dan mengatur role pengguna VECTRA.",
    content: [
      { type: "heading", text: "Sistem Role di VECTRA" },
      { type: "steps", items: [
        "Super Admin: Akses penuh ke semua fitur termasuk pengaturan sistem.",
        "Org Admin: Kelola pengguna dan rapat dalam organisasi.",
        "Moderator: Buat dan kelola rapat, tidak bisa kelola pengguna.",
        "Host: Buat dan pimpin rapat, kelola transkripsi.",
        "Participant (Peserta): Bergabung ke rapat live, enroll voiceprint.",
        "Viewer: Lihat rapat dan laporan, tidak bisa membuat rapat baru.",
        "Auditor: Lihat laporan dan audit trail untuk kepatuhan.",
      ]},
      { type: "heading", text: "Cara Menambah Pengguna Baru" },
      { type: "steps", items: [
        "Buka menu User & Role dari sidebar (khusus admin).",
        "Klik tombol + Tambah Pengguna.",
        "Isi nama, email, divisi, dan pilih role.",
        "Klik Simpan — pengguna bisa login dengan password sementara.",
      ]},
      { type: "tip", text: "Berikan role Peserta untuk staf yang hanya perlu mengikuti rapat live, bukan mengelola konten rapat." },
    ],
  },
  {
    id: "adm-2", category: "admin", readTime: 3,
    title: "Audit Trail dan Log Aktivitas",
    summary: "Memahami sistem audit trail VECTRA untuk kepatuhan dan keamanan.",
    content: [
      { type: "heading", text: "Apa itu Audit Trail?" },
      { type: "paragraph", text: "VECTRA mencatat setiap aksi penting yang dilakukan pengguna: login, buat rapat, upload audio, generate summary, export laporan, dan perubahan pengaturan. Log ini tersimpan permanen dan tidak bisa dihapus." },
      { type: "heading", text: "Melihat Log Aktivitas" },
      { type: "steps", items: [
        "Buka menu Reports dari sidebar.",
        "Klik tab Audit Trail.",
        "Filter berdasarkan tanggal, pengguna, atau jenis aksi.",
        "Export log ke CSV untuk keperluan audit eksternal.",
      ]},
      { type: "tip", text: "Aktifkan notifikasi untuk aksi sensitif (login dari lokasi baru, export data massal) di menu Settings > Notifikasi." },
    ],
  },

  // ── TIPS & TRIK ───────────────────────────────────────────────────────────
  {
    id: "tip-1", category: "tips", readTime: 5,
    title: "Tips Transkripsi yang Akurat",
    summary: "Praktik terbaik untuk mendapatkan hasil transkripsi yang paling akurat dari VECTRA.",
    content: [
      { type: "heading", text: "Persiapan Teknis" },
      { type: "steps", items: [
        "Gunakan mikrofon dedicated (bukan built-in laptop) di ruangan rapat.",
        "Posisikan mikrofon di tengah meja, bukan di tepi.",
        "Gunakan headset atau lavalier mic untuk rapat hibrida.",
        "Pastikan koneksi internet stabil saat live meeting.",
        "Rekam dalam format WAV atau MP3 320kbps untuk upload.",
      ]},
      { type: "heading", text: "Selama Rapat" },
      { type: "steps", items: [
        "Satu orang berbicara dalam satu waktu — hindari interupsi berlebihan.",
        "Bicara dengan jelas dan kecepatan normal.",
        "Sebutkan nama sebelum bicara untuk memudahkan identifikasi speaker.",
        "Hindari menggunakan akronim yang tidak standar tanpa penjelasan.",
        "Pastikan semua peserta terdaftar dan voiceprint-nya sudah di-enroll.",
      ]},
      { type: "heading", text: "Pasca Rapat" },
      { type: "steps", items: [
        "Review transkrip segera setelah rapat untuk koreksi manual jika diperlukan.",
        "Generate AI Summary setelah transkripsi selesai untuk notulen otomatis.",
        "Tandai action items sebagai selesai saat tugas sudah dikerjakan.",
      ]},
      { type: "tip", text: "Whisper AI bekerja paling baik dengan audio di atas 128kbps. Untuk rapat hibrida, pertimbangkan software perekaman khusus yang memisahkan channel audio per peserta." },
    ],
  },
  {
    id: "tip-2", category: "tips", readTime: 3,
    title: "Mengoptimalkan Pengenalan Pembicara",
    summary: "Cara memaksimalkan akurasi identifikasi pembicara dalam transkripsi real-time.",
    content: [
      { type: "heading", text: "Faktor Kualitas Voiceprint" },
      { type: "paragraph", text: "Akurasi pengenalan pembicara dipengaruhi oleh kualitas voiceprint yang didaftarkan dan kondisi audio saat rapat." },
      { type: "heading", text: "Tips Enrollment" },
      { type: "steps", items: [
        "Enroll voiceprint di kondisi yang sama dengan rapat sebenarnya (ruangan, mikrofon).",
        "Bicara dengan nada dan kecepatan normal saat enrollment.",
        "Rekam ulang jika kualitas voiceprint di bawah 75%.",
        "Hindari rekam voiceprint saat sakit atau kelelahan (suara berubah).",
      ]},
      { type: "heading", text: "Optimasi Saat Meeting" },
      { type: "steps", items: [
        "Pastikan semua peserta dalam jangkauan mikrofon yang sama.",
        "Kurangi latar suara: matikan AC, tutup jendela jika perlu.",
        "Jika 2 orang memiliki suara mirip, pastikan keduanya sudah enroll voiceprint.",
      ]},
      { type: "tip", text: "Sistem menggunakan kombinasi suara dan konteks kalimat untuk identifikasi. Semakin banyak rapat, semakin 'familiar' sistem dengan pola bicara Anda." },
    ],
  },
  {
    id: "tip-3", category: "tips", readTime: 2,
    title: "Shortcut dan Fitur Tersembunyi",
    summary: "Temukan cara-cara cepat dan fitur tersembunyi VECTRA yang meningkatkan produktivitas.",
    content: [
      { type: "heading", text: "Navigasi Cepat" },
      { type: "steps", items: [
        "Gunakan Search (sidebar) untuk pencarian global lintas semua rapat dan transkrip.",
        "Klik nama pembicara di transkrip untuk memfilter segmennya saja.",
        "Gunakan Calendar untuk melihat semua rapat dalam tampilan bulan.",
      ]},
      { type: "heading", text: "Tips AI yang Efektif" },
      { type: "steps", items: [
        "Generate Summary langsung setelah transkripsi — hasilnya lebih akurat.",
        "Tanyakan spesifik ke AI Chat: sebutkan topik atau nama orang.",
        "Re-generate Summary jika hasil pertama kurang memuaskan.",
      ]},
      { type: "heading", text: "Export & Berbagi" },
      { type: "steps", items: [
        "Export PDF untuk laporan formal ke manajemen.",
        "Export CSV untuk analisis data di Excel atau Google Sheets.",
        "Gunakan Export JSON untuk integrasi dengan sistem lain.",
      ]},
      { type: "tip", text: "VECTRA menggunakan enkripsi AES-256 untuk semua data yang disimpan. Data Anda tidak pernah keluar dari infrastruktur on-premise Anda." },
    ],
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Content renderer
// ─────────────────────────────────────────────────────────────────────────────
function ArticleContent({ content }) {
  return (
    <div className="prose prose-sm max-w-none space-y-4">
      {content.map((block, i) => {
        if (block.type === "paragraph") return (
          <p key={i} className="text-slate-700 leading-relaxed">{block.text}</p>
        );
        if (block.type === "heading") return (
          <h3 key={i} className="font-semibold text-slate-900 mt-5 mb-2">{block.text}</h3>
        );
        if (block.type === "steps") return (
          <ol key={i} className="space-y-2">
            {(block.items || []).map((item, j) => (
              <li key={j} className="flex gap-3">
                <span className="flex-shrink-0 h-5 w-5 rounded-full grad-brand text-white text-[10px] font-bold grid place-items-center mt-0.5">{j + 1}</span>
                <span className="text-slate-700 text-sm leading-relaxed">{item}</span>
              </li>
            ))}
          </ol>
        );
        if (block.type === "tip") return (
          <div key={i} className="flex gap-3 rounded-xl bg-amber-50 border border-amber-200 p-4">
            <Lightbulb size={16} className="mt-0.5 shrink-0 text-amber-600" />
            <p className="text-sm text-amber-800">{block.text}</p>
          </div>
        );
        return null;
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Article card component
// ─────────────────────────────────────────────────────────────────────────────
function ArticleCard({ article, onOpen }) {
  const cat = CATEGORIES.find((c) => c.id === article.category);
  return (
    <button
      className="w-full text-left clay p-5 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 group cursor-pointer"
      onClick={() => onOpen(article)}
      data-testid={`article-card-${article.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            {cat && <span className={`flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider ${cat.color}`}><cat.icon size={11} />{cat.label}</span>}
            <span className="text-[10px] text-slate-400 flex items-center gap-1"><Clock size={10} />{article.readTime} menit</span>
          </div>
          <h3 className="text-sm font-semibold text-slate-900 group-hover:text-brand-700 transition-colors leading-snug mb-1.5">{article.title}</h3>
          <p className="text-xs text-slate-500 line-clamp-2 leading-relaxed">{article.summary}</p>
        </div>
        <ArrowRight size={15} className="shrink-0 mt-1 text-slate-300 group-hover:text-brand-600 group-hover:translate-x-1 transition-all" />
      </div>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────────────
export default function KnowledgeCenter() {
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [openArticle, setOpenArticle] = useState(null);

  const filtered = useMemo(() => {
    let result = ARTICLES;
    if (activeCategory !== "all") result = result.filter((a) => a.category === activeCategory);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (a) => a.title.toLowerCase().includes(q) || a.summary.toLowerCase().includes(q)
      );
    }
    return result;
  }, [activeCategory, searchQuery]);

  const counts = useMemo(() => {
    const map = { all: ARTICLES.length };
    CATEGORIES.slice(1).forEach((c) => {
      map[c.id] = ARTICLES.filter((a) => a.category === c.id).length;
    });
    return map;
  }, []);

  return (
    <div className="space-y-6 max-w-7xl mx-auto" data-testid="knowledge-center-page">
      {/* Header */}
      <div className="clay p-7 bg-gradient-to-br from-brand-600 via-brand-700 to-violet-700 text-white">
        <div className="flex items-center gap-3 mb-4">
          <span className="grid place-items-center h-11 w-11 rounded-xl bg-white/15 backdrop-blur">
            <BookOpen size={22} />
          </span>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Knowledge Center</h1>
            <p className="text-blue-100 text-sm mt-0.5">Panduan lengkap penggunaan VECTRA AI Meeting Intelligence</p>
          </div>
        </div>
        {/* Search */}
        <div className="relative max-w-xl">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50" />
          <input
            type="text"
            placeholder="Cari artikel panduan…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full h-11 pl-10 pr-4 rounded-xl bg-white/15 border border-white/20 backdrop-blur text-white placeholder-white/50 text-sm focus:outline-none focus:ring-2 focus:ring-white/40"
            data-testid="knowledge-search"
          />
          {searchQuery && (
            <button className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white" onClick={() => setSearchQuery("")}>
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      <div className="flex gap-6">
        {/* Sidebar Categories */}
        <aside className="w-56 shrink-0" data-testid="knowledge-sidebar">
          <div className="clay p-3 space-y-1">
            {CATEGORIES.map((cat) => {
              const active = cat.id === activeCategory;
              return (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  data-testid={`category-${cat.id}`}
                  className={`w-full flex items-center justify-between gap-2.5 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200 ${
                    active ? "grad-brand text-white shadow-sm" : "text-slate-600 hover:bg-slate-50 hover:text-brand-700"
                  }`}
                >
                  <span className="flex items-center gap-2.5">
                    <cat.icon size={15} className={active ? "text-white" : cat.color} />
                    {cat.label}
                  </span>
                  <span className={`text-[10px] font-bold rounded-full px-1.5 py-0.5 ${active ? "bg-white/20 text-white" : "bg-slate-100 text-slate-500"}`}>
                    {counts[cat.id] || 0}
                  </span>
                </button>
              );
            })}
          </div>
        </aside>

        {/* Main content */}
        <div className="flex-1 min-w-0">
          {openArticle ? (
            // Article detail view
            <div className="clay p-7" data-testid="article-detail">
              <button
                onClick={() => setOpenArticle(null)}
                className="flex items-center gap-2 text-sm text-slate-500 hover:text-brand-700 mb-6 transition-colors"
                data-testid="back-to-articles"
              >
                <ChevronRight size={14} className="rotate-180" /> Kembali ke daftar artikel
              </button>

              {(() => {
                const cat = CATEGORIES.find((c) => c.id === openArticle.category);
                return (
                  <>
                    <div className="flex items-center gap-2 mb-3">
                      {cat && (
                        <span className={`flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider ${cat.color}`}>
                          <cat.icon size={12} /> {cat.label}
                        </span>
                      )}
                      <span className="text-xs text-slate-400 flex items-center gap-1"><Clock size={11} /> {openArticle.readTime} menit baca</span>
                    </div>
                    <h2 className="text-xl font-bold text-slate-900 mb-2">{openArticle.title}</h2>
                    <p className="text-slate-500 text-sm mb-6 leading-relaxed">{openArticle.summary}</p>
                    <div className="border-t border-slate-100 pt-6">
                      <ArticleContent content={openArticle.content} />
                    </div>
                  </>
                );
              })()}
            </div>
          ) : (
            // Article list
            <>
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-slate-500">
                  {filtered.length} artikel
                  {searchQuery && <span> untuk "<strong>{searchQuery}</strong>"</span>}
                  {activeCategory !== "all" && !searchQuery && <span> dalam <strong>{CATEGORIES.find((c) => c.id === activeCategory)?.label}</strong></span>}
                </p>
                {searchQuery && (
                  <button className="text-xs text-brand-600 hover:underline" onClick={() => setSearchQuery("")}>Hapus filter</button>
                )}
              </div>

              {filtered.length === 0 ? (
                <div className="clay p-12 text-center">
                  <Search size={32} className="mx-auto text-slate-300 mb-3" />
                  <p className="font-medium text-slate-600">Artikel tidak ditemukan</p>
                  <p className="text-sm text-slate-400 mt-1">Coba kata kunci lain atau pilih kategori berbeda</p>
                  <button className="mt-4 text-sm text-brand-600 hover:underline" onClick={() => { setSearchQuery(""); setActiveCategory("all"); }}>
                    Tampilkan semua artikel
                  </button>
                </div>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2" data-testid="article-list">
                  {filtered.map((a) => <ArticleCard key={a.id} article={a} onOpen={setOpenArticle} />)}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
